<template>
    <!-- Search filters -->
    <div class="search-filters" :class="{ 'sidebar': isSidebar }" v-show="isVisible">
        <span class="heading">
            Zahlung
        </span>

        <!-- Refundable -->
        <div class="form-group d-flex rate-toggler">
            <span class="label">
                <strong>
                    Voll erstattungsfähig
                </strong>
                <small class="subtitle">
                    Die Unterkunft kann kostenfrei storniert oder umgebucht werden.
                </small>
            </span>

            <button type="button" class="btn btn-toggle btn-toggle-lg ml-auto" :class="{ active: filters.is_refundable }" @click="() => filters.is_refundable = ! filters.is_refundable" aria-pressed="false" autocomplete="off" id="cancellationPoliciesToggler">
                <div class="handle"></div>
            </button>
        </div>

        <div class="divider"></div>

        <div v-if="additionalFilters.indexOf('package') !== -1 || additionalFilters.indexOf('member') !== -1">
            <span class="heading">
                {{ 'search.filters.typeOfRate' | trans }}
            </span>

            <!-- Package -->
            <div class="form-group d-flex rate-toggler" v-if="additionalFilters.indexOf('package') !== -1">
                <span class="label">
                    <strong>
                        {{ 'search.filters.packageTitle' | trans }}
                    </strong>
                    <small class="subtitle">
                        {{ 'search.filters.packageSubtitle' | trans }}
                    </small>
                </span>

                <button type="button" class="btn btn-toggle btn-toggle-lg ml-auto" :class="{ active: filters.is_package }" @click="() => filters.is_package = ! filters.is_package" aria-pressed="false" autocomplete="off" id="packageRateToggler">
                    <div class="handle"></div>
                </button>
            </div>

            <!-- Member -->
            <div class="form-group d-flex rate-toggler" v-if="additionalFilters.indexOf('member') !== -1">
                <span class="label">
                    <strong>
                        {{ 'search.filters.memberTitle' | trans }}
                    </strong>
                    <small class="subtitle">
                        {{ 'search.filters.memberSubtitle' | trans }}
                    </small>
                </span>

                <button type="button" class="btn btn-toggle btn-toggle-lg ml-auto" :class="{ active: filters.is_member }" @click="() => filters.is_member = ! filters.is_member" aria-pressed="false" autocomplete="off" id="memberRateToggler">
                    <div class="handle"></div>
                </button>
            </div>

            <div class="divider"></div>
        </div>

        <!-- Stars -->
        <span class="heading">
            {{ 'search.filters.hotelStars' | trans }}
        </span>

        <div class="stars" v-if="filters.property_stars">
            <div class="btn-group">
                <button type="button" class="btn btn-default" :class="{ 'active': filters.property_stars.indexOf(1) !== -1 }" @click="toggleStars(1)">
                    <span>1</span>
                    <i class="fas fa-star"></i>
                </button>
                <button type="button" class="btn btn-default" :class="{ 'active': filters.property_stars.indexOf(2) !== -1 }" @click="toggleStars(2)">
                    <span>2</span>
                    <i class="fas fa-star"></i>
                </button>
                <button type="button" class="btn btn-default" :class="{ 'active': filters.property_stars.indexOf(3) !== -1 }" @click="toggleStars(3)">
                    <span>3</span>
                    <i class="fas fa-star"></i>
                </button>
                <button type="button" class="btn btn-default" :class="{ 'active': filters.property_stars.indexOf(4) !== -1 }" @click="toggleStars(4)">
                    <span>4</span>
                    <i class="fas fa-star"></i>
                </button>
                <button type="button" class="btn btn-default" :class="{ 'active': filters.property_stars.indexOf(5) !== -1 }" @click="toggleStars(5)">
                    <span>5</span>
                    <i class="fas fa-star"></i>
                </button>
            </div>
        </div>

        <div v-if="amenities && amenities.length > 0">
            <div class="divider"></div>

            <!-- Amenities -->
            <span class="heading">
                {{ 'search.filters.amenities' | trans }}
            </span>

            <div class="property-types">
                <div class="form-check" v-for="amenity in isAmenitiesExpanded ? amenities : amenities.slice(0, maxItems)">
                    <input class="form-check-input" type="checkbox" :value="amenity.id" v-model="filters.amenities" :id="'amenity_' + amenity.id">
                    <label class="form-check-label" :for="'amenity_' + amenity.id" :class="{ 'text-muted': filters.amenities.length > 0 && filters.amenities.indexOf(amenity.id) === -1, 'text-dark': ! (filters.amenities.length > 0 && filters.amenities.indexOf(amenity.id) === -1) }">
                        <span>
                            {{ amenity.name }}
                        </span>

                        <small class="count" v-if="amenity.property_count < 300">
                            {{ amenity.property_count | formatNumber }}
                        </small>
                        <small class="count" v-if="amenity.property_count >= 300">
                            300+
                        </small>
                    </label>
                </div>
            </div>

            <!-- Toggle -->
            <button type="button" class="btn btn-default mt-2" @click="isAmenitiesExpanded = ! isAmenitiesExpanded" v-if="amenities.length > maxItems">
                <span v-if="! isAmenitiesExpanded">
                    {{ 'search.filters.showAll' | trans }}
                </span>
                <span v-if="isAmenitiesExpanded">
                    {{ 'search.filters.minimize' | trans }}
                </span>
            </button>

            <!-- Reset: Amenities -->
            <button type="button" class="btn btn-link text-dark px-4 mt-1 btn-sm" @click="resetAmenities" v-if="filters.amenities.length > 0">
                {{ 'search.filters.reset' | trans }}
            </button>
        </div>

        <div class="divider"></div>

        <!-- Distance to center -->
        <span class="heading">
            {{ 'search.filters.distanceToCenterLabel' | trans }}
        </span>
        <div class="property-types">
            <div class="form-check" v-for="distance in possibleDistanceToCenterValues" :key="'distance_' + String(distance)">
                <input class="form-check-input" type="checkbox" v-model="filters.distance_to_center" :id="'distance_' + String(distance)" :value="distance">
                <label class="form-check-label" :for="'distance_' + String(distance)">
                    {{ 'search.filters.distanceToCenterText' | trans({ distance: distance / 1000 }) }}
                </label>
            </div>
        </div>

        <div class="divider"></div>

        <!-- Ratings -->
        <span class="heading">
            {{ 'search.filters.ratingsLabel' | trans }}
        </span>

        <div class="custom-control custom-radio" v-for="rating in possibleRatingValues" :key="'rating_' + String(rating).replace('.', '')">
            <input class="custom-control-input" type="radio" v-model="filters.ratings" :id="'rating_' + String(rating).replace('.', '')" :value="rating">
            <label class="custom-control-label" :for="'rating_' + String(rating).replace('.', '')">
                <span>
                    {{ ratingsText(rating) + (! isNaN(rating) && rating !== null ? ':' : '') }}
                </span>
                <span v-if="! isNaN(rating) && rating !== null">
                    {{ rating | formatRatings }}+
                </span>
            </label>
        </div>

        <div class="divider"></div>

        <!-- Price range -->
        <span class="heading">
            {{ 'search.filters.priceLabel' | trans }}
        </span>

        <div class="form-check form-check-vertical" v-for="(range, rangeIndex) in priceRange">
            <input class="form-check-input" type="checkbox" v-model="filters.price_range" :id="'price_range' + rangeIndex" :value="range.value">
            <label class="form-check-label" :for="'price_range' + rangeIndex">
                {{ range.label }}
            </label>
        </div>

        <div v-if="propertyTypes.length > 0">
            <div class="divider"></div>

            <!-- Property types -->
            <span class="heading">
                {{ 'search.filters.propertyTypes' | trans }}
            </span>

            <div class="property-types">
                <div class="form-check" v-for="propertyType in isPropertyTypesExpanded ? propertyTypes : propertyTypes.slice(0, maxItems)">
                    <input class="form-check-input" type="checkbox" :value="propertyType.id" v-model="filters.property_types" :id="'property_type_' + propertyType.id">
                    <label class="form-check-label" :for="'property_type_' + propertyType.id" :class="{ 'text-muted': filters.property_types.length > 0 && filters.property_types.indexOf(propertyType.id) === -1, 'text-dark': ! (filters.property_types.length > 0 && filters.property_types.indexOf(propertyType.id) === -1) }">
                        <span>
                            {{ propertyType.name }}
                        </span>

                        <small class="count" v-if="propertyType.property_count < 300">
                            {{ propertyType.property_count | formatNumber }}
                        </small>
                        <small class="count" v-if="propertyType.property_count >= 300">
                            300+
                        </small>
                    </label>
                </div>
            </div>

            <!-- Toggle -->
            <button type="button" class="btn btn-default mt-2" @click="isPropertyTypesExpanded = ! isPropertyTypesExpanded" v-if="propertyTypes.length > maxItems">
                <span v-if="! isPropertyTypesExpanded">
                    {{ 'search.filters.showAll' | trans }}
                </span>
                <span v-if="isPropertyTypesExpanded">
                    {{ 'search.filters.minimize' | trans }}
                </span>
            </button>

            <!-- Reset: Property types -->
            <button type="button" class="btn btn-link text-dark px-4 mt-1 btn-sm" @click="resetPropertyTypes" v-if="filters.property_types.length > 0">
                {{ 'search.filters.reset' | trans }}
            </button>
        </div>

        <!-- Spacer -->
        <div style="height: 3rem;"></div>

        <button type="button" class="btn btn-default d-none d-md-block" @click="reset">
            {{ 'search.filters.reset' | trans }}
        </button>
    
        <div class="footer-controls d-flex d-md-none">
            <button type="button" class="btn btn-default" @click="reset">
                {{ 'search.filters.reset' | trans }}
            </button>

            <button type="button" class="btn btn-default close-button" @click="$emit('close')">
                <i class="fa fa-spinner-third fa-spin" v-if="isLoading"></i>
                <span v-if="! isLoading">
                    <span v-if="total < 300">
                        {{ total | formatNumber }}
                    </span>
                    <span v-if="total >= 300">
                        300+
                    </span>
                    
                    {{ 'search.filters.results' | trans }}
                </span>
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        props: [
        'isSidebar',
        'isLoading',
        'isVisible',
        'originFilters',
        'propertyTypes',
        'amenities',
        'additionalFilters',
        'total',
        'nights',
        ],
        data() {
            return {
                isMounted: false,

                filters: {
                    is_refundable: false,
                    is_package: false,
                    is_member: false,
                    property_types: [],
                    amenities: [],
                    stars: [],
                    ratings: null,
                    price_range: [],
                    distance_to_center: [],
                },
                defaultFilters: {},
                possibleRatingValues: [null, 4.5, 4, 3.5],
                possibleDistanceToCenterValues: [1000,3000,5000,20000],
                isPropertyTypesExpanded: false,
                isAmenitiesExpanded: false,
                windowWidth: 0,
            }
        },
        mounted() {
            this.defaultFilters = Object.assign({}, this.filters)
            this.filters = Object.assign({}, this.originFilters)

            this.$nextTick(() => {
                window.addEventListener('resize', this.onResize, {
                    passive: true,
                });
            })

            this.isMounted = true
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
        },
        computed: {
            maxItems() {
                return this.windowWidth > 768 ? 12 : 6
            },
            priceRange() {
                const nights = this.nights || 1

                return [{
                    value: [0, nights * 50],
                    label: 'Unter ' + this.$options.filters.formatPrice(nights * 50) + ' €',
                }, {
                    value: [nights * 50, nights * 100],
                    label: this.$window.Lang.get('search.priceRangeLabel', {
                        from: this.$options.filters.formatPrice(nights * 50),
                        to: this.$options.filters.formatPrice(nights * 100),
                        currency: '€',
                    }),
                }, {
                    value: [nights * 100, nights * 150],
                    label: this.$window.Lang.get('search.priceRangeLabel', {
                        from: this.$options.filters.formatPrice(nights * 100),
                        to: this.$options.filters.formatPrice(nights * 150),
                        currency: '€',
                    }),
                }, {
                    value: [nights * 150, nights * 225],
                    label: this.$window.Lang.get('search.priceRangeLabel', {
                        from: this.$options.filters.formatPrice(nights * 150),
                        to: this.$options.filters.formatPrice(nights * 225),
                        currency: '€',
                    }),
                }, {
                    value: [nights * 225, 99999],
                    label: 'Über ' + this.$options.filters.formatPrice(nights * 225) + ' €',
                }]
            },
        },
        methods: {
            onResize() {
                this.windowWidth = window.innerWidth
            },

            // Stars
            toggleStars(quantity) {
                const index = this.filters.property_stars.indexOf(quantity)

                if (index === -1) {
                    this.filters.property_stars.push(quantity)
                } else {
                    this.filters.property_stars.splice(index, 1)
                }
            },

            // Property types
            resetPropertyTypes() {
                this.filters.property_types = []
            },

            // Amenities
            resetAmenities() {
                this.filters.amenities = []
            },

            // Ratings
            ratingsText(rating) {
                if (rating >= 3.0 && rating < 4.0) {
                    return this.$window.Lang.get('search.ratings.good')
                } else if (rating >= 4.0 && rating < 4.3) {
                    return this.$window.Lang.get('search.ratings.veryGood')
                } else if (rating >= 4.3 && rating < 4.5) {
                    return this.$window.Lang.get('search.ratings.outstanding')
                } else if (rating >= 4.5 && rating < 4.7) {
                    return this.$window.Lang.get('search.ratings.wonderful')
                } else if (rating >= 4.7) {
                    return this.$window.Lang.get('search.ratings.extraordinary')
                }

                return this.$window.Lang.get('search.ratings.any');
            },

            // Reset
            reset() {
                this.filters = this.defaultFilters
            },
        },
        watch: {
            originFilters: {
                handler(val) {
                    this.filters = Object.assign({}, this.originFilters)
                },
                deep: true,
            },
            filters: {
                handler(val, oldValue) {
                    if (this.isMounted && ! this.$lodash.isEqual(this.originFilters, this.filters)) {
                        this.$emit('change', val)
                    }
                },
                deep: true,
            },
        }
    }
</script>
